import React, { memo } from 'react';
import { dispatch, dispatchSync } from 'nike-re-framejs';
import i18n from '@i18n';

import Overview from './NikeOneOverview';
import { ErrorBoundaryX } from '@/common/components/error/ErrorBoundary';
import NikeOneTournament, { NikeOneMenuItemTournament } from './NikeOneTournament';
import NikeOneContext, { PREMATCH, PREMATCH_FAV } from '../context/NikeOneContext';
import { isLoggedIn } from '@/account';
import { normalizeSportEventId } from '../domain/sportevent';
import useClock from '../hook/useClock';
import useCashinOffer from '../hook/useCashinOffer';
import useRefreshBoxes from '../hook/useRefreshBoxes';
import useRefreshMenu from '../hook/useRefreshMenu';
import {
    NIKE1,
    NIKE1_DETAIL,
    NIKE1_FAV,
    NIKE1_MATCH,
    NIKE1_MENU_ITEM_TOURNAMENT,
    NIKE1_RESULTS,
    NIKE1_TOURNAMENT
} from '@/routing/routes';
import { useActiveRoute } from 'nike-ui/src/common/routing/NikeRouteContext';
import { clear, initializePrematch, resultsOnly } from '@/nikeone/handlers';
import NikeOneDetail from '@/nikeone/views/NikeOneDetail';
import BetLocationContext, { betLocation } from 'nike-ui/src/common/BetLocationContext';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';

import { RequiresNikeone } from 'nike-ui/src/components/nikeone/RequiresNikeone';
import { ModuleFallback } from '@/common/components/ModuleFallback';

/**
 *  Root path component; params: [boxId, betNumber, eventId, sportSlugPart, countrySlugPart, tournamentSlugPart]
 */
const NikeOneNg = memo(function NikeOneNg(props) {
    useClock('nikeone.tick');
    useCashinOffer();
    useRefreshMenu();
    useRefreshBoxes();

    const favorites = props.route.path.startsWith('/favorites');
    const isLogged = isLoggedIn();
    return (
        <NikeOneContext.Provider value={favorites ? PREMATCH_FAV : PREMATCH}>
            <div className="nikeone-offer nikeone-offer-prematch">
                {favorites && !isLogged ? (
                    <EmptyState
                        text={i18n('Pre zobrazenie obsahu sa musíte prihlásiť')}
                        buttonText={i18n('jsp.login.Prihlasit')}
                        buttonClassName="nui-button secondary"
                        buttonOnClick={() => dispatch(['login.openScreen'])}
                    />
                ) : (
                    <NikeOneSwitch {...props} />
                )}
            </div>
        </NikeOneContext.Provider>
    );
});

const NikeOneSwitch = memo(function NikeOneSwitch({
    boxId,
    betId,
    eventId,
    sportSlugPart,
    countrySlugPart,
    tournamentSlugPart
}) {
    const route = useActiveRoute();
    return (
        <ErrorBoundaryX
            description={
                'NikeOne Hybrid Content :' + route.name + betId + eventId + sportSlugPart + countrySlugPart + boxId
            }
        >
            {route === NIKE1_MATCH ? (
                <BetLocationContext.Provider value={{ betLocationSection: betLocation.DETAIL }}>
                    <NikeOneDetail sportEventId={normalizeSportEventId(eventId)} />
                </BetLocationContext.Provider>
            ) : route === NIKE1_DETAIL ? (
                <BetLocationContext.Provider value={{ betLocationSection: betLocation.DETAIL }}>
                    <NikeOneDetail sportEventId={normalizeSportEventId(eventId)} />
                </BetLocationContext.Provider>
            ) : route === NIKE1_TOURNAMENT ? (
                <NikeOneTournament boxId={boxId} />
            ) : route === NIKE1_MENU_ITEM_TOURNAMENT ? (
                <NikeOneMenuItemTournament
                    countrySlugPart={countrySlugPart}
                    sportSlugPart={sportSlugPart}
                    tournamentSlugPart={tournamentSlugPart}
                />
            ) : route === NIKE1_RESULTS ? (
                <Overview sportSlug={sportSlugPart} countrySlug={countrySlugPart} results />
            ) : route === NIKE1 ? (
                <Overview sportSlug={sportSlugPart} countrySlug={countrySlugPart} />
            ) : route === NIKE1_FAV ? (
                <Overview sportSlug={sportSlugPart} countrySlug={countrySlugPart} />
            ) : null}
        </ErrorBoundaryX>
    );
});

class NikeOneClass extends React.Component {
    constructor(props) {
        super();
        this.state = { results: props.route === NIKE1_RESULTS };
        // this assure that only if there is no other instance of this component, store will be cleaned during the unmount
        this.uuid = Math.round(Math.random() * 100000);
    }

    // neprepisovat na didMount (ani useEffect) - musi byt willMount - NEMENIT!!
    UNSAFE_componentWillMount() {
        dispatchSync(initializePrematch('PREMATCH' + this.uuid));
        if (this.state.results) {
            dispatchSync(resultsOnly(true));
        }
    }

    UNSAFE_componentWillUpdate(nextProps) {
        // this.state.results represents a state machine
        // RESULTS -> NO RESULTS; SIGNAL: nikeOne
        // RESULTS <- NO RESULTS; SIGNAL: nikeOneResults
        // all other signals are ignored
        if (this.state.results && nextProps.route === NIKE1) {
            this.setState({ results: false });
            dispatchSync(resultsOnly(false));
        }
        if (!this.state.results && nextProps.route === NIKE1_RESULTS) {
            this.setState({ results: true });
            dispatchSync(resultsOnly(true));
        }
    }

    componentWillUnmount() {
        dispatch(clear('PREMATCH' + this.uuid));
    }

    render() {
        return <NikeOneNg {...this.props} key={this.state.results} />;
    }
}

export const NikeOne = props => {
    return (
        <RequiresNikeone fallback={<ModuleFallback msg={'nui.module.missing.nikeone'} />}>
            <NikeOneClass {...props} />
        </RequiresNikeone>
    );
};
